import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { CompliancePendingFilters, ComplianceSavedFilter } from './types';

interface SaveFilterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendingFilters: CompliancePendingFilters;
  existingFilters: ComplianceSavedFilter[];
  onSave: (name: string, description?: string) => void;
  editingFilter: ComplianceSavedFilter | null;
}

export const SaveFilterModal: React.FC<SaveFilterModalProps> = ({
  open,
  onOpenChange,
  pendingFilters,
  existingFilters,
  onSave,
  editingFilter,
}) => {
  const [filterName, setFilterName] = useState('');
  const [filterDescription, setFilterDescription] = useState('');
  const [nameError, setNameError] = useState('');

  const maxNameLength = 50;
  const maxDescriptionLength = 200;

  // Load editing filter data
  useEffect(() => {
    if (editingFilter) {
      setFilterName(editingFilter.name);
      setFilterDescription(editingFilter.description || '');
    } else {
      setFilterName('');
      setFilterDescription('');
    }
    setNameError('');
  }, [editingFilter, open]);

  // Validate filter name
  useEffect(() => {
    if (!filterName.trim()) {
      setNameError('');
      return;
    }

    // Check for duplicate names (excluding current filter if editing)
    const isDuplicate = existingFilters.some(
      (filter) =>
        filter.name.toLowerCase() === filterName.trim().toLowerCase() &&
        filter.id !== editingFilter?.id
    );

    if (isDuplicate) {
      setNameError('A filter with this name already exists');
    } else {
      setNameError('');
    }
  }, [filterName, existingFilters, editingFilter]);

  const handleSave = () => {
    if (!filterName.trim()) {
      setNameError('Filter name is required');
      return;
    }

    if (nameError) {
      return;
    }

    onSave(filterName.trim(), filterDescription.trim() || undefined);
    onOpenChange(false);
  };

  // Build filter summary
  const filterSummary: Array<{ label: string; values: string[] }> = [];

  if (pendingFilters.selectedStatuses.length > 0) {
    filterSummary.push({
      label: 'Compliance Status',
      values: pendingFilters.selectedStatuses,
    });
  }
  if (pendingFilters.selectedTypes.length > 0) {
    filterSummary.push({
      label: 'Compliance Type',
      values: pendingFilters.selectedTypes,
    });
  }
  if (pendingFilters.selectedRiskLevels.length > 0) {
    filterSummary.push({
      label: 'Risk Level',
      values: pendingFilters.selectedRiskLevels,
    });
  }
  if (pendingFilters.selectedDepartments.length > 0) {
    filterSummary.push({
      label: 'Department',
      values: pendingFilters.selectedDepartments,
    });
  }
  if (pendingFilters.selectedAssignedTo.length > 0) {
    filterSummary.push({
      label: 'Assigned To',
      values: pendingFilters.selectedAssignedTo,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editingFilter ? 'Edit Filter' : 'Save Filter'}</DialogTitle>
          <DialogDescription>
            {editingFilter
              ? 'Update the name and description of your saved filter.'
              : 'Save your current filter configuration for quick access later.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Filter Name */}
          <div className="space-y-2">
            <Label htmlFor="filter-name">
              Filter Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="filter-name"
              placeholder="e.g., High Risk Non-Compliant Assets"
              value={filterName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterName(e.target.value)}
              maxLength={maxNameLength}
              className={nameError ? 'border-destructive' : ''}
              autoFocus
            />
            <div className="flex items-center justify-between">
              {nameError ? (
                <p className="text-xs text-destructive">{nameError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Give your filter a descriptive name
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {filterName.length}/{maxNameLength}
              </p>
            </div>
          </div>

          {/* Filter Description */}
          <div className="space-y-2">
            <Label htmlFor="filter-description">Description (Optional)</Label>
            <Textarea
              id="filter-description"
              placeholder="Add a description to help you remember what this filter is for..."
              value={filterDescription}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFilterDescription(e.target.value)
              }
              rows={3}
              maxLength={maxDescriptionLength}
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Optional description for this filter
              </p>
              <p className="text-xs text-muted-foreground">
                {filterDescription.length}/{maxDescriptionLength}
              </p>
            </div>
          </div>

          {/* Filter Configuration Summary */}
          <div className="space-y-2">
            <Label>Filter Configuration</Label>
            <div className="rounded-md border bg-muted/30 p-4">
              {filterSummary.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-2">
                  No filters configured
                </p>
              ) : (
                <ScrollArea className="max-h-[200px]">
                  <div className="space-y-3">
                    {filterSummary.map((item, index) => (
                      <div key={index} className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {item.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground pl-1">
                          {item.values.join(', ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              This configuration will be saved with your filter
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!filterName.trim() || !!nameError}
          >
            {editingFilter ? 'Update Filter' : 'Save Filter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
